/**
 * Save an API `blob()` envelope (or, with `downloadText`, a browser-built string) as a file.
 *
 * The API sends binary base64-encoded inside the JSON envelope rather than
 * streaming it, so every download decodes here rather than at each call site.
 *
 * Two details that look like ceremony and are not:
 *
 * - **The anchor is appended to the document.** Firefox ignores a programmatic
 *   `click()` on a detached element.
 * - **The object URL is revoked on a later tick.** Revoking synchronously after
 *   `click()` races the download: Firefox and some Android webviews have not
 *   started reading the blob yet and abort with a failed download.
 *
 * Native caveat: this is the plain web path. Inside a Capacitor webview Android
 * saves the file and iOS opens it in the system viewer — both acceptable, but a
 * true "save to Files" flow needs `@capacitor/filesystem` + `@capacitor/share`.
 */

export interface DownloadableBlob {
  /** Base64-encoded file contents. */
  data: string
  filename: string
  contentType: string
}

export function downloadBlob(payload: DownloadableBlob): void {
  const binary = atob(payload.data)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }

  saveBlob(new Blob([bytes], { type: payload.contentType }), payload.filename)
}

export interface DownloadableText {
  /** File contents as a string (JSON, CSV, …) — no base64 step. */
  text: string
  filename: string
  contentType: string
}

/**
 * Save a string built in the browser as a file — the sibling of `downloadBlob`
 * for content that never came from an API `blob()` envelope (e.g. a JSON
 * export assembled from several paged responses).
 */
export function downloadText(payload: DownloadableText): void {
  saveBlob(new Blob([payload.text], { type: payload.contentType }), payload.filename)
}

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.rel = 'noopener'
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  setTimeout(() => URL.revokeObjectURL(url), 0)
}
